import React, { useState, useCallback, useMemo } from "react"
import { useForm } from "react-hook-form"
import type { ProductSelection } from "../../types/registration"
import { Button } from "@/components/ui/button"
import * as Switch from "@radix-ui/react-switch";
import { Checkbox } from "@/components/ui/checkbox"
import Lottie from 'react-lottie';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Edit2, Check, FileText } from "lucide-react"
import { useMutation, useQuery } from "react-query"
import { finalRegistrationStep, getCommissionByProductId } from "pages/AssistedPartnerRegistration/services/api"
import { Img, Spinner } from "components";
import Alert from "models/Alert/alert";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import animationData from './Success-Check.json';

interface ProductSelectionFormProps {
  onSubmit: (data: ProductSelection) => void
  onPrevious: () => void
  initialData?: Partial<ProductSelection>
  onNext: () => void
  defaultCompletedSteps?: Set<number>;
}

interface MarginProduct {
  product: string
  margin: number
  modifiedMargin?: number
  currency?: string
  isEditing?: boolean
  id: string
}

interface IForm {
  [productId: string]: MarginProduct[]
}

// Constants moved outside component to prevent recreation
const MOCK_PRODUCTS = [
  { id: "1", name: "Remittance", description: "International money transfer services" },
  { id: "2", name: "Forex", description: "Foreign exchange services" },
  { id: "3", name: "Visa", description: "Visa processing and support" },
  { id: "4", name: "Study Abroad", description: "Education abroad consultancy" },
] as const

const CURRENCY_OPTIONS = [
  { value: "INR", label: "INR" },
  { value: "USD", label: "USD" },
] as const

// Stepper component
const StepperItem = ({ title, isCompleted, isActive }: { title: string; isCompleted: boolean; isActive: boolean }) => (
  <div className="flex items-center space-x-3 mb-6">
    <div
      className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${isCompleted ? "bg-green-500 text-white" : isActive ? "bg-[#165fe3] text-white" : "bg-gray-300 text-gray-600"
        }`}
    >
      {isCompleted ? <Check className="w-4 h-4" /> : isActive ? "4" : ""}
    </div>
    <span
      className={`text-sm font-medium ${isCompleted ? "text-green-600" : isActive ? "text-[#165fe3]" : "text-gray-500"
        }`}
    >
      {title}
    </span>
  </div>
)

// Memoized components
const ProductCard = React.memo<{
  product: (typeof MOCK_PRODUCTS)[number]
  isSelected: boolean
  onToggle: (productId: string, checked: boolean) => void;
  disabled?: boolean
}>(({ product, isSelected, onToggle, disabled =false }) => (
  <div
    className={` ${disabled ? "opacity-50 cursor-not-allowed" : ""} border-2 rounded-lg p-4 transition-all ${isSelected ? "border-[#165fe3] bg-blue-50" : "border-gray-300 hover:border-gray-400"
      }`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <span className="font-medium text-gray-900">{product.name}</span>
      </div>
      <Switch.Root
        disabled={disabled}
        className="w-[45px] h-[25px] bg-blue_gray-lite-bg rounded-full  border-2 border-blue-700 outline-none cursor-default"
        id="airplane-mode"
        style={{ WebkitTapHighlightColor: "rgba(0, 0, 0, 0)" }}
        checked={isSelected}
        onCheckedChange={(checked) => onToggle(product.id, checked)}
      >
        <Switch.Thumb
          className={`flex p-1 w-[20px] h-[20px] bg-blue-700_01 rounded-full shadow-black-900 transition-transform duration-100 ${isSelected && "translate-x-5"} will-change-transform`}
        />
      </Switch.Root>
    </div>
  </div>
))

const MarginProductRow = React.memo<{
  marginProduct: MarginProduct
  productKey: string
  onEdit: (productKey: string, id: string) => void
  onValueChange: (productKey: string, id: string, value: number) => void
  onInputBlur: (productKey: string, id: string) => void
}>(({ marginProduct, productKey, onEdit, onValueChange, onInputBlur }) => {
  const hasModification = useMemo(
    () => marginProduct.modifiedMargin !== undefined && marginProduct.modifiedMargin !== marginProduct.margin,
    [marginProduct.modifiedMargin, marginProduct.margin],
  )

  const displayValue = useMemo(
    () => (hasModification ? marginProduct.modifiedMargin : marginProduct.margin || 0),
    [hasModification, marginProduct.modifiedMargin, marginProduct.margin],
  )

  return (
    <div className="grid grid-cols-2 gap-5 items-center py-3 border-b border-gray-100 last:border-b-0">
      <div className="text-sm text-gray-900 font-medium">{marginProduct.product}</div>
      <div className="flex items-center space-x-3">
        {marginProduct.isEditing ? (
          <>
            {/* <div className="flex items-center space-x-2">
              <Select defaultValue={marginProduct.currency || "INR"}>
                <SelectTrigger className="w-20 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={marginProduct.modifiedMargin || 0}
                onChange={(e) => onValueChange(productKey, marginProduct.id, Number.parseFloat(e.target.value) || 0)}
                // onBlur={() => onInputBlur(productKey, marginProduct.id)}
                className="w-20 h-8 text-sm"
                autoFocus
              />
              {hasModification && (
                <div className="text-xs">
                  <span className="bg-blue-100 text-[#165fe3] px-2 py-0.5 rounded text-xs">Proposed</span>
                  <div className="text-gray-500 mt-0.5">
                    {marginProduct.currency || "INR"} {marginProduct.margin || 0} Previous value
                  </div>
                </div>
              )}
            </div> */}
            <div className="flex flex-col items-center justify-between gap-2 w-full">
              <div className="flex w-full items-center border border-gray-300 rounded-md overflow-hidden  p-1 h-11">
                <Select
                  // onValueChange={(val) => handleMarginChange(product.id, 'currency', val)}
                  disabled={!marginProduct.isEditing}
                >
                  <SelectTrigger className="w-[70px] disabled:cursor-default h-9 text-sm border-none text-blue-700 bg-[#E1EBFC] rounded-md focus:ring-0">
                    <SelectValue placeholder="INR" />
                  </SelectTrigger>
                  <SelectContent className="">
                    <SelectItem className="text-blue-700" value="commission">
                      INR
                    </SelectItem>
                    <SelectItem className="text-blue-700" value="flat">
                      Flat
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={displayValue}
                  type="number"
                  key={marginProduct.id}
                  min="0"
                  step="0.01"
                  onWheelCapture={(e) => {
                    e.currentTarget.blur();
                  }}
                  // onBlur={() => onInputBlur(productKey, marginProduct.id)}
                  onChange={(e) => onValueChange(productKey, marginProduct.id, Number.parseFloat(e.target.value) || 0)}
                  disabled={!marginProduct.isEditing}
                  className={`w-full h-9 text-sm border-none rounded-none ${marginProduct.isEditing ? "text-[#333333]" : "text-gray-400"
                    }`}
                />
                {hasModification ? (
                  <span className="text-xs text-gray-400 pr-2">Proposed</span>
                ) : (
                  <span className="text-xs text-gray-400 pr-2">Default</span>
                )}
              </div>
              {hasModification && (
                <div className="flex w-full justify-between">
                  <small className="text-[#B5B5B5] font-normal text-xs leading-[140%]">
                    INR {marginProduct.margin}
                  </small>
                  <small className="text-[#B5B5B5] font-normal text-xs leading-[140%]">
                    Previous value
                  </small>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center space-x-2 bg-[#F6F6F6] gap-10 px-3 w-full rounded-md py-1">
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600 font-medium leading-[130%] tracking-[0.1px]">{marginProduct.currency || "INR"}</span>
              <span className="text-sm font-medium">{displayValue}</span>
            </div>
            {hasModification && (
              <div className="text-xs">
                <span className="bg-blue-100 text-[#165fe3] px-2 py-0.5 rounded text-xs">Proposed</span>
                <div className="text-gray-500 mt-0.5">
                  {marginProduct.currency || "INR"} {marginProduct.margin || 0} Previous value
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(productKey, marginProduct.id)}
              className="text-[#165fe3] hover:text-[#165fe3] hover:bg-blue-50 p-1 h-auto"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
})

export const ProductSelectionForm: React.FC<ProductSelectionFormProps> = () => {
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])
  const [showSuccess, setShowSuccess] = useState(false)
  const [shareCredentials, setShareCredentials] = useState(false)

  const form = useForm<IForm>({
    defaultValues: {},
    mode: "onChange",
  })
  const navigate = useNavigate()

   const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };


  // Only watch the selected product to minimize re-renders
  const currentProductData = form.watch(selectedProducts[0])

  // Memoized handlers
  const handleProductToggle = useCallback((productId: string, checked: boolean) => {
    setSelectedProducts((prev) => (checked ? [...prev, productId] : prev.filter((id) => id !== productId)))
  }, [])

  const handleEditStart = useCallback(
    (productKey: string, id: string) => {
      const currentValues = form.getValues(productKey)
      if (!currentValues) return

      const updatedValues = currentValues.map((item) =>
        item.id === id ? { ...item, isEditing: true, modifiedMargin: item.modifiedMargin ?? item.margin } : item,
      )
      form.setValue(productKey, updatedValues)
    },
    [form],
  )

  const handleValueChange = useCallback(
    (productKey: string, id: string, value: number) => {
      const currentValues = form.getValues(productKey)
      if (!currentValues) return

      const updatedValues = currentValues.map((item) => (item.id === id ? { ...item, modifiedMargin: value } : item))
      form.setValue(productKey, updatedValues)
    },
    [form],
  )

  const handleInputBlur = useCallback(
    (productKey: string, id: string) => {
      const currentValues = form.getValues(productKey)
      if (!currentValues) return

      const updatedValues = currentValues.map((item) => (item.id === id ? { ...item, isEditing: false } : item))
      form.setValue(productKey, updatedValues)
    },
    [form],
  )

  const { mutate, isLoading } = useMutation({
    mutationFn: finalRegistrationStep,
    onSuccess: () => {
      setShowSuccess(true)
      // naviagte auto after 5 seconds
      setTimeout(() => {
        navigate('/partnerdetails/' + localStorage.getItem('currentPartnerRegistrationId'));
        localStorage.removeItem('currentPartnerRegistrationId');
      }, 5000)
    },
    onError: (error: any) => {
      console.error("Submission failed:", error)
    },
  })

  const handleFormSubmit = () => {
    const data = form.getValues();
    const rates = Object.values(data).flatMap((item) => item.map((i) => ({
      purpose_id: i.id,
      charges: i.modifiedMargin ?? i.margin,
      id: selectedProductId
    })));
    mutate({
      accept_default: false,
      unapproved_commision_rates: [],
      approved_commision_rates: rates,
      share_cred: shareCredentials
    })
  }

  // Optimized query with proper dependency
  const selectedProductId = selectedProducts[0]
  const { data: commissionRatesData } = useQuery({
    queryKey: ["getCommissionByProductId", selectedProductId],
    queryFn: () => getCommissionByProductId(selectedProductId),
    enabled: !!selectedProductId,
    onSuccess: (data) => {
      const dataToSet = data.map((item: any) => ({
        product: item._purpose_mapper.name,
        margin: item.charges,
        modifiedMargin: item.charges,
        currency: item.Country_Currency_ISO,
        isEditing: false,
        id: item._purpose_mapper.id.toString(),
      }))
      form.setValue(selectedProductId, dataToSet)
    },
    staleTime: 60 * 1000, // 1 minute
    cacheTime: 60 * 1000, // 1 minute
  })

  // Memoized computed values
  const isRemittanceSelected = useMemo(() => selectedProducts.includes("1"), [selectedProducts])

  const isSubmitDisabled = useMemo(() => selectedProducts.length === 0, [selectedProducts.length])

  return (
    <>
      {isLoading && <Spinner />}
      <div className="max-w-7xl h-[500px] mx-auto px-6">
        <div className="grid gap-8 sm:grid-cols-1 lg:grid-cols-4 sm:grid-flow-col">

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Choose Product</h2>
            </div>

            <div className="flex gap-8 bg-white-A700 h-[500px] overflow-hidden rounded-lg p-6 shadow-sm">
              {/* Product Selection */}
              <div className="max-w-[350px] w-full justify-between h-full flex flex-col">
                <div className="space-y-4 ">
                  {MOCK_PRODUCTS.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      isSelected={selectedProducts.includes(product.id)}
                      onToggle={handleProductToggle}
                      disabled={product.id !== "1"} // Disable if Remittance and no data
                    />
                  ))}
                </div>

                {/* Share Credentials Checkbox */}
                <div className="flex items-center w-full py-3 px-2.5 gap-2.5 bg-[#FAFAFA]">
                  <input type='checkbox' className="rounded-[4px] border-[#999999] border-2" id="share-credentials" checked={shareCredentials} onChange={() => setShareCredentials(!shareCredentials)} />
                  <label htmlFor="share-credentials" className="text-sm font-medium text-[#666666] cursor-pointer leading-4 -tracking-[2%]">
                    Share Credentials with partner
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between pt-6">
                  <Button
                    onClick={handleFormSubmit}
                    disabled={isSubmitDisabled}
                    className="bg-[#165fe3] text-white-A700 hover:bg-[#165fe3]/90"
                  >
                    Finish
                  </Button>
                </div>
              </div>

              {/* Margin Table */}
              {isRemittanceSelected && currentProductData && (
                <div className="flex-1 h-[400px]">
                  <div className="bg-[#E1EBFC] rounded-t-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm font-medium text-gray-700">
                      <div>Product</div>
                      <div>Margin on IBR</div>
                    </div>
                  </div>
                  <div className="bg-white-A700 border-x border-b rounded-b-lg px-4 h-full overflow-y-auto">
                    {currentProductData.map((marginProduct) => (
                      <MarginProductRow
                        key={marginProduct.id}
                        marginProduct={marginProduct}
                        productKey={selectedProductId}
                        onEdit={handleEditStart}
                        onValueChange={handleValueChange}
                        onInputBlur={handleInputBlur}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Instructions Panel */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg h-fit border border-[#E4E4E4] p-4">
              <h4 className="font-medium text-gray-900 mb-4">Instructions</h4>
              <div className="space-y-4 text-sm text-gray-600">
                {isRemittanceSelected && <p>Margins are in addition to IBR rate</p>}
                <p>Select the products you want to offer to your customers.</p>
                <p>You can modify these settings later from your dashboard.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccess && (
          <AlertDialog open={true} onOpenChange={setShowSuccess}>
            <AlertDialogPortal>
              <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
              <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-[668px]">              
                <div className="p-10 px-8">
                  <div className="flex justify-between w-full items-center">
                    <h1 className="text-blue-600 text-3xl font-bold ">
                      It's all set !
                    </h1>
                    <div className="w-24 h-24  rounded-full flex items-center justify-center">
                      <Lottie options={defaultOptions}  />
                    </div>
                  </div>
                  <div className="space-y-4 mb-8">
                    <div className="text-gray-600">
                      <span className="font-medium">Partner ID</span> {localStorage.getItem('currentPartnerRegistrationId')}
                    </div>
                  </div>

                  <div className="mb-8 bg-[#F9F9F9] p-5 rounded-2xl">
                    <h3 className="text-gray-800 font-medium text-lg mb-4">
                      Now, the Partner will :
                    </h3>
                    <div className="space-y-2.5">
                      {
                        shareCredentials &&
                        <>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                              <Img src="/images/email.png" alt="Email Icon" className="w-6 h-6" />
                            </div>
                            <span className="text-gray-700">Automatically receive login credentials on .</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                              <Img src="/images/user.png" alt="Email Icon" className="w-6 h-6" />
                            </div>
                            <span className="text-gray-700">Be required to set their own password on first login.</span>
                          </div>
                        </>
                      }
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                          <Img src="/images/e-sign.png" alt="Email Icon" className="w-6 h-6" />
                        </div>
                        <span className="text-gray-700">eSign contract on first time login.</span>
                      </div>
                    </div>
                  </div>

                  <AlertDialogFooter className="flex justify-end p-0">
                    <AlertDialogCancel
                      className="bg-blue-600 w-fit text-white-A700 px-8 py-2 rounded-md font-medium hover:bg-blue-700 border-0"
                      onClick={() => {
                        navigate('/partnerdetails/' + localStorage.getItem('currentPartnerRegistrationId'));
                        localStorage.removeItem('currentPartnerRegistrationId');
                      }}
                    >
                      Close
                    </AlertDialogCancel>
                  </AlertDialogFooter>
                </div>
              </AlertDialogContent>
            </AlertDialogPortal>
          </AlertDialog>
        )}
      </div>
    </>
  )
}
